import React, { useState } from "react";
import Swal from "sweetalert2";
import { useAppDispatch } from "../../_App/Redux/hooks";
import { deletePlaningItem } from "../../_App/Redux/Slices/plannings/planningsSlice";
import PreventifeForm from "./PreventifeForm";

// Type d'événement
interface Event {
  id: number;
  title: string;
  month: number;
  year: number;
  color?: string;
  description?: string;
  data: any;
}

interface YearlyCalendarProps {
  events: Event[];
  reload: boolean;
  setReload: any;
}

const YearlyCalendar: React.FC<YearlyCalendarProps> = ({
  events,
  reload,
  setReload,
}) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const deleteEvent = async (code: any) => {
    const result = await Swal.fire({
      title: "Voulez-vous confirmer ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Supprimer!",
      cancelButtonText: "Annuler",
    });

    if (result.isConfirmed) {
      try {
        const res = await dispatch(deletePlaningItem(code));
        if (res) {
          Swal.fire({
            title: "Supprimé !",
            icon: "success",
          });
          setReload(!reload);
        }
      } catch (error) {
        console.error("Error deleting event:", error);
        Swal.fire({
          title: "Error!",
          text: "There was an issue deleting the event.",
          icon: "error",
        });
      }
    }
  };

  const eventsByMonth = Array.from({ length: 12 }, (_, month) => ({
    month,
    events: events.filter(
      (event) => event.year === selectedYear && event.month === month
    ),
  }));

  const [showModal, setShowModal] = useState(false);
  const [date, setDate] = useState<any>(undefined);

  const handleClose = () => {
    setShowModal(false);
  };

  const goToPreviousYear = () => {
    setSelectedYear(prev => prev - 1);
  };

  const goToNextYear = () => {
    setSelectedYear(prev => prev + 1);
  };

  const goToCurrentYear = () => {
    setSelectedYear(currentYear);
  };

  return (
    <div>
      <PreventifeForm
        showModal={showModal}
        handleClose={handleClose}
        date={date}
        reload={reload}
        setReload={setReload}
      />

      {/* Year Navigation Bar */}
      <div style={styles.navigationBar}>
        <button onClick={goToPreviousYear} style={styles.navButton}>
          ← {selectedYear - 1}
        </button>
        
        <div style={styles.yearSelector}>
          <button onClick={goToPreviousYear} style={styles.chevronButton}>
            ←
          </button>
          <span style={styles.currentYear}>
            {selectedYear}
           
          </span>
          <button onClick={goToNextYear} style={styles.chevronButton}>
            →
          </button>
        </div>
        
        <div style={styles.rightButtons}>
          {selectedYear !== currentYear && (
            <button onClick={goToCurrentYear} style={styles.currentYearButton}>
              Année actuelle
            </button>
          )}
          <button onClick={goToNextYear} style={styles.navButton}>
            {selectedYear + 1} →
          </button>
        </div>
      </div>

      <div style={styles.calendarGrid}>
        {eventsByMonth.map(({ month, events }) => (
          <div
            key={month}
            style={{
              ...styles.monthCell,
              border:
                selectedYear === currentYear && month === currentMonth 
                  ? "2px solid #007bff" 
                  : "1px solid #ddd",
              borderRadius: "10px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              padding: "15px",
              transition: "transform 0.3s",
              cursor: "pointer",
            }}
          >
            <h3
              style={{
                ...styles.monthTitle,
                textTransform: "capitalize",
                fontWeight: "bold",
                color: selectedYear === currentYear && month === currentMonth 
                  ? "#007bff" 
                  : "#333",
              }}
              onClick={() => {
                setDate({ month: month + 1, year: selectedYear });
                setShowModal(true);
              }}
            >
              {new Date(selectedYear, month).toLocaleString("fr-FR", {
                month: "long",
              })}
            </h3>
            <hr style={{ border: "0.5px solid #ddd" }} />
            {events.length > 0 ? (
              <ul style={{ padding: 0 }}>
                {events.map((event) => (
                  <li
                    onClick={() => {
                      window.location.href =
                        "/preventifChecklists/" +
                        event.data.id +
                        "/" +
                        event.data.machine_code;
                    }}
                    key={event.id}
                    style={{
                      ...styles.eventItem,
                      backgroundColor: event.color || "#e0f7fa",
                      borderRadius: "8px",
                      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      cursor: "pointer",
                      transition: "background-color 0.3s, transform 0.3s",
                      margin: "7px",
                    }}
                    title={event.description || "Aucun détail disponible"}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.transform = "scale(1.05)")
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.transform = "scale(1)")
                    }
                    aria-label={event.description || "Event details"}
                  >
                    <div className="row" style={{ width: "100%" }}>
                      <div className="col-10" style={{ display: "flex" }}>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: "bold",
                            fontSize: "16px",
                          }}
                        >
                          {event.title}
                        </p>
                      </div>
                      <div className="col-2">
                        <i
                          className="fas fa-trash-alt text-danger"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent navigation when clicking delete
                            deleteEvent(event.data.code);
                          }}
                          aria-label="Delete event"
                        ></i>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p
                style={{
                  ...styles.noEvents,
                  color: "#999",
                  fontStyle: "italic",
                }}
              >
                Aucun événement
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  navigationBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    backgroundColor: "#f5f5f5",
    borderBottom: "1px solid #ddd",
    marginBottom: "16px",
  },
  navButton: {
    padding: "8px 16px",
    backgroundColor: "#007bff",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "background-color 0.3s",
  },
  yearSelector: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  chevronButton: {
    padding: "8px 16px",
    backgroundColor: "#6c757d",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    transition: "background-color 0.3s",
  },
  currentYear: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#333",
    minWidth: "120px",
    textAlign: "center",
  },
  currentYearBadge: {
    fontSize: "14px",
    fontWeight: "normal",
    color: "#007bff",
  },
  rightButtons: {
    display: "flex",
    gap: "12px",
  },
  currentYearButton: {
    padding: "8px 16px",
    backgroundColor: "#28a745",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "background-color 0.3s",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
    padding: "16px",
  },
  monthCell: {
    borderRadius: "8px",
    padding: "12px",
    textAlign: "center",
    cursor: "pointer",
    transition: "0.3s",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
  },
  monthTitle: {
    fontSize: "1.2em",
    marginBottom: "8px",
    color: "#333",
  },
  eventList: {
    marginTop: "8px",
    fontSize: "0.9em",
  },
  eventCount: {
    fontSize: "0.9em",
    color: "#007bff",
    fontWeight: "bold",
    marginBottom: "8px",
    display: "block",
  },
  noEvents: {
    fontSize: "0.9em",
    color: "#777",
  },
  eventItem: {
    // This is just a placeholder, actual styles are inline
  },
};

export default YearlyCalendar;